import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import GoogleIcon from '@mui/icons-material/Google';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PlaceIcon from '@mui/icons-material/Place';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import AuthContext from '../../context/authContext';
import useOpeningHoursOverview from '../../hooks/useOpeningHoursOverview';
import useGoogleBusiness from '../../hooks/useGoogleBusiness';
import GoogleRatingMatchDialog from '../Entertainment/GoogleRatingMatchDialog';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

// Highlight colour for a day whose Google hours differ from our internal hours.
const DISCREPANCY_BG = 'rgba(211, 47, 47, 0.10)';

const SPECIAL_TYPE_LABEL = {
  holiday: 'Feiertag',
  vacation: 'Ferien',
  summerVacation: 'Sommerferien',
  bridgeDay: 'Brückentag',
};

const fmtDate = iso => dayjs(iso).format('DD.MM.YYYY');

// Compact label for a segment's date range; open-ended at the horizon edge.
const segmentRangeLabel = (segment, horizon) => {
  if (horizon && segment.to === horizon.to) return `ab ${fmtDate(segment.from)}`;
  return `${fmtDate(segment.from)} – ${fmtDate(segment.to)}`;
};

const fmtIntervals = intervals => {
  if (!Array.isArray(intervals) || intervals.length === 0) return 'Geschlossen';
  return intervals.map(iv => `${iv.start}–${iv.end}`).join(', ');
};

// Builds an editable week map ({ '0': [{start,end}], … }) from the per-day
// Google comparison rows, reading either the Google side or the internal side.
const daysToWeek = (days, side) => {
  const week = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
  (days || []).forEach(d => {
    week[d.weekday] = (d[side] || []).map(iv => ({ start: iv.start, end: iv.end }));
  });
  return week;
};

// Editor dialog: lets the user adjust the Google opening hours of one
// entertainment (per-day intervals) and either save them or prefill from our
// internal schedule.
function GoogleHoursEditDialog({ open, item, onClose, onSave, saving }) {
  // week: { weekday -> [{ start, end }] }
  const [week, setWeek] = useState(() => daysToWeek(item?.days, 'google'));

  useEffect(() => {
    if (open) setWeek(daysToWeek(item?.days, 'google'));
  }, [open, item]);

  const orderedDays = useMemo(
    () => (item?.days || []).map(d => ({ weekday: d.weekday, label: d.label })),
    [item]
  );

  const setDay = (weekday, intervals) => setWeek(prev => ({ ...prev, [weekday]: intervals }));

  const addInterval = weekday =>
    setDay(weekday, [...(week[weekday] || []), { start: '10:00', end: '22:00' }]);

  const removeInterval = (weekday, idx) =>
    setDay(weekday, (week[weekday] || []).filter((_, i) => i !== idx));

  const updateInterval = (weekday, idx, field, value) =>
    setDay(
      weekday,
      (week[weekday] || []).map((iv, i) => (i === idx ? { ...iv, [field]: value } : iv))
    );

  const toggleClosed = (weekday, closed) =>
    setDay(weekday, closed ? [] : [{ start: '10:00', end: '22:00' }]);

  const loadInternal = () => setWeek(daysToWeek(item?.days, 'internal'));

  const handleSave = () => onSave(week);

  if (!item) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Google-Öffnungszeiten bearbeiten
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          {item.longName}
          {item.locationTitle ? ` · ${item.locationTitle}` : ''}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} size='small'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            Diese Zeiten werden direkt im Google-Business-Profil gespeichert.
          </Typography>
          {item.hasInternal && (
            <Button size='small' startIcon={<SyncAltIcon />} onClick={loadInternal}>
              Interne Zeiten laden
            </Button>
          )}
        </Box>

        {orderedDays.map(({ weekday, label }) => {
          const intervals = week[weekday] || [];
          const closed = intervals.length === 0;
          return (
            <Box key={weekday} sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant='body2' sx={{ fontWeight: 600, width: 110 }}>
                  {label}
                </Typography>
                <Switch
                  size='small'
                  checked={!closed}
                  onChange={e => toggleClosed(weekday, !e.target.checked)}
                />
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  {closed ? 'Geschlossen' : 'Geöffnet'}
                </Typography>
                {!closed && (
                  <Tooltip title='Intervall hinzufügen'>
                    <IconButton size='small' onClick={() => addInterval(weekday)}>
                      <AddIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {intervals.map((iv, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, ml: '110px' }}>
                  <TextField
                    type='time'
                    size='small'
                    value={iv.start}
                    onChange={e => updateInterval(weekday, idx, 'start', e.target.value)}
                    inputProps={{ step: 300 }}
                  />
                  <Typography variant='body2'>–</Typography>
                  <TextField
                    type='time'
                    size='small'
                    value={iv.end}
                    onChange={e => updateInterval(weekday, idx, 'end', e.target.value)}
                    inputProps={{ step: 300 }}
                  />
                  <IconButton size='small' onClick={() => removeInterval(weekday, idx)}>
                    <DeleteOutlineIcon fontSize='small' />
                  </IconButton>
                </Box>
              ))}
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color='inherit'>
          Abbrechen
        </Button>
        <Button
          variant='contained'
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} color='inherit' /> : null}
        >
          Auf Google speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const WEEKDAY_SHORT = { 0: 'So', 1: 'Mo', 2: 'Di', 3: 'Mi', 4: 'Do', 5: 'Fr', 6: 'Sa' };

const normalizeIntervals = intervals =>
  (intervals || [])
    .map(iv => ({ start: iv.start, end: iv.end }))
    .sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));

// Impact panel: shows the edited schedule's metadata and which entertainments
// (per branch) a direct in-place change affects.
function HoursImpactPanel({ loading, impact, weekdayLabel }) {
  if (loading) {
    return (
      <Stack direction='row' spacing={1} alignItems='center' sx={{ color: 'text.secondary' }}>
        <CircularProgress size={16} />
        <Typography variant='body2'>Auswirkung wird ermittelt…</Typography>
      </Stack>
    );
  }
  if (!impact) return null;
  return (
    <Box>
      <Stack direction='row' spacing={0.75} alignItems='center' flexWrap='wrap' useFlexGap sx={{ mb: 1 }}>
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          Plan „{impact.name}"
        </Typography>
        <Chip
          size='small'
          variant='outlined'
          label={`gültig ${dayjs(impact.validFrom).format('DD.MM.YYYY')} – ${dayjs(impact.validTo).format('DD.MM.YYYY')}`}
        />
        <Chip size='small' variant='outlined' label={`Priorität ${impact.priority}`} />
        {(impact.specialTypes || []).map(t => (
          <Chip key={t} size='small' color='info' variant='outlined' label={SPECIAL_TYPE_LABEL[t] || t} />
        ))}
      </Stack>
      {(impact.sharedWeekdays || []).length > 0 && (
        <Alert severity='info' variant='outlined' sx={{ mb: 1 }}>
          Der Plan definiert diese Zeiten gemeinsam mit{' '}
          <b>{impact.sharedWeekdays.map(d => WEEKDAY_SHORT[d]).join(', ')}</b>. Die Änderung gilt nur
          für <b>{weekdayLabel}</b> – die anderen Tage behalten ihre Zeiten.
        </Alert>
      )}
      <Typography variant='body2' sx={{ mb: 1 }}>
        Der Plan ist <b>{impact.assignmentCount} Entertainment(s)</b> zugewiesen. Eine direkte
        Änderung wirkt sich auf alle folgenden aus – überall dort, wo dieser Plan greift:
      </Typography>
      <Stack spacing={0.5}>
        {(impact.locations || []).map(loc => (
          <Box key={loc.branchId}>
            <Typography variant='body2' sx={{ fontWeight: 600 }}>
              {loc.branchName}
            </Typography>
            {loc.entertainments.length > 0 && (
              <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                {loc.entertainments.join(', ')}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

// Dialog to edit the internal opening hours of one weekday (one matrix cell),
// then either apply in place (affects every usage of the schedule) or decouple
// the change into a new schedule for this entertainment only.
function HoursEditDialog({ open, context, onClose, onSaved }) {
  const { getOpeningHourImpact, updateOpeningHourDay, decoupleOpeningHourDay } =
    useOpeningHoursOverview();
  const [intervals, setIntervals] = useState([]);
  const [impact, setImpact] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    if (!open || !context) return;
    setIntervals(normalizeIntervals(context.intervals));
    setNewName(context.sourceName ? `${context.sourceName} (angepasst)` : 'Neuer Öffnungszeiten-Plan');
    setImpact(null);
    setImpactLoading(true);
    getOpeningHourImpact(context.sourceId, context.weekday)
      .then(res => res && setImpact(res))
      .catch(() => setImpact(null))
      .finally(() => setImpactLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, context]);

  if (!context) return null;

  const closed = intervals.length === 0;
  const hasChanges =
    JSON.stringify(normalizeIntervals(intervals)) !==
    JSON.stringify(normalizeIntervals(context.intervals));
  // Both abilities come from the impact response: a branch-scoped editor may
  // only change a schedule that belongs to their own branches alone, and never
  // decouple. `canEditInPlace` is absent on older backends — assume allowed
  // there, the save is still checked server-side.
  const canApply = impact ? impact.canEditInPlace !== false : true;
  const canDecouple =
    Boolean(impact && impact.assignmentCount > 1) && impact?.canDecouple !== false;
  // Only a branch-scoped editor is blocked by a shared schedule; without the
  // right the cell would not have been clickable in the first place.
  const blockedByOtherBranch = Boolean(impact && !canApply);

  const addInterval = () => setIntervals(prev => [...prev, { start: '10:00', end: '22:00' }]);
  const removeInterval = idx => setIntervals(prev => prev.filter((_, i) => i !== idx));
  const updateInterval = (idx, field, value) =>
    setIntervals(prev => prev.map((iv, i) => (i === idx ? { ...iv, [field]: value } : iv)));
  const toggleClosed = isClosed => setIntervals(isClosed ? [] : [{ start: '10:00', end: '22:00' }]);

  const validIntervals = () =>
    intervals.every(iv => iv.start && iv.end && iv.start < iv.end) || intervals.length === 0;

  const handleApply = async () => {
    if (!validIntervals()) {
      toast.error('Ungültiges Zeitintervall – Ende muss nach Beginn liegen');
      return;
    }
    setSaving(true);
    try {
      await updateOpeningHourDay(context.sourceId, context.weekday, intervals);
      toast.success('Öffnungszeiten aktualisiert');
      onSaved();
    } catch (e) {
      toast.error('Fehler beim Aktualisieren der Öffnungszeiten');
    } finally {
      setSaving(false);
    }
  };

  const handleDecouple = async () => {
    if (!validIntervals()) {
      toast.error('Ungültiges Zeitintervall – Ende muss nach Beginn liegen');
      return;
    }
    setSaving(true);
    try {
      await decoupleOpeningHourDay(context.sourceId, {
        entertainmentId: context.entertainmentId,
        weekday: context.weekday,
        intervals,
        newName: newName || undefined,
      });
      toast.success('Änderung in neuen Öffnungszeiten-Plan ausgekoppelt');
      onSaved();
    } catch (e) {
      toast.error('Fehler beim Auskoppeln der Öffnungszeiten-Änderung');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        Öffnungszeiten bearbeiten
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          {context.entertainmentName} · {context.weekdayLabel} · {context.segmentLabel}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Switch size='small' checked={!closed} onChange={e => toggleClosed(!e.target.checked)} />
          <Typography variant='body2'>{closed ? 'Geschlossen' : 'Geöffnet'}</Typography>
          {!closed && (
            <Tooltip title='Intervall hinzufügen'>
              <IconButton size='small' onClick={addInterval}>
                <AddIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {intervals.map((iv, idx) => (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TextField
              type='time'
              size='small'
              value={iv.start}
              onChange={e => updateInterval(idx, 'start', e.target.value)}
              inputProps={{ step: 300 }}
            />
            <Typography variant='body2'>–</Typography>
            <TextField
              type='time'
              size='small'
              value={iv.end}
              onChange={e => updateInterval(idx, 'end', e.target.value)}
              inputProps={{ step: 300 }}
            />
            <IconButton size='small' onClick={() => removeInterval(idx)}>
              <DeleteOutlineIcon fontSize='small' />
            </IconButton>
          </Box>
        ))}
        {closed && (
          <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
            Der Tag wird in diesem Plan explizit als geschlossen markiert (er fällt nicht auf einen
            anderen Plan zurück).
          </Typography>
        )}
        {blockedByOtherBranch ? (
          <Alert severity='warning' sx={{ mb: 1 }}>
            Dieser Öffnungszeiten-Plan gilt auch für andere Standorte. Mit deinem Recht kannst du nur
            Pläne ändern, die ausschließlich deinem eigenen Standort zugeordnet sind — sonst würdest
            du die Öffnungszeiten der anderen Standorte mitverstellen. Wende dich an die Verwaltung,
            damit für deinen Standort ein eigener Plan angelegt wird.
          </Alert>
        ) : (
          !hasChanges && (
            <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
              Ändere die Zeiten, um „Übernehmen"{canDecouple ? ' oder „Auskoppeln"' : ''} zu
              aktivieren.
            </Typography>
          )
        )}

        <Divider sx={{ my: 2 }}>Auswirkung</Divider>
        <HoursImpactPanel loading={impactLoading} impact={impact} weekdayLabel={context.weekdayLabel} />

        {canDecouple && (
          <>
            <Divider sx={{ my: 2 }}>Auskoppeln</Divider>
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1.5 }}>
              Beim Auskoppeln wird die Änderung in einen <b>neuen Öffnungszeiten-Plan</b> übernommen,
              der nur <b>{context.entertainmentName}</b> zugewiesen ist. Alle anderen Entertainments
              behalten den bisherigen Plan unverändert.
            </Typography>
            <TextField
              label='Name des neuen Plans'
              size='small'
              fullWidth
              value={newName}
              onChange={e => setNewName(e.target.value)}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} disabled={saving} color='inherit'>
          Abbrechen
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        {canDecouple && (
          <Button
            onClick={handleDecouple}
            disabled={saving || !hasChanges}
            variant='outlined'
            startIcon={<CallSplitIcon />}
          >
            Auskoppeln
          </Button>
        )}
        <Button
          onClick={handleApply}
          disabled={saving || !hasChanges || !canApply}
          variant='contained'
        >
          Übernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Google status chip for one entertainment.
function GoogleStatusChip({ google, mapped }) {
  if (!mapped) {
    return <Chip size='small' variant='outlined' label='Nicht mit Google verknüpft' />;
  }
  if (!google) return null;
  if (google.configured === false) {
    return <Chip size='small' color='warning' variant='outlined' label='Google nicht konfiguriert' />;
  }
  if (google.error) {
    return <Chip size='small' color='error' variant='outlined' icon={<WarningAmberIcon />} label='Google-Fehler' />;
  }
  if (google.hasDiscrepancy) {
    return <Chip size='small' color='error' icon={<WarningAmberIcon />} label='Google weicht ab' />;
  }
  if (google.hasInternal) {
    return <Chip size='small' color='success' icon={<CheckCircleIcon />} label='Google synchron' />;
  }
  return <Chip size='small' variant='outlined' label='Kein interner Plan' />;
}

// The core matrix: weekdays as rows, one column per time span in which the
// effective hours are constant, plus (when available) a Google column showing
// the hours currently stored on Google with discrepancies in red.
function SegmentsTable({ item, horizon, canEditSource, onEditCell }) {
  const segments = item.segments || [];
  const google = item.google && item.google.configured !== false ? item.google : null;
  const showGoogleCol = Boolean(google && item.googleLocationName && !google.error);

  const googleByWeekday = useMemo(() => {
    const map = new Map();
    (google?.days || []).forEach(d => map.set(d.weekday, d));
    return map;
  }, [google]);

  // Weekday rows in Monday-first order, taken from the first segment (all
  // segments share the same order); fall back to the Google days.
  const dayRows = segments.length ? segments[0].days : google?.days || [];

  if (!segments.length && !showGoogleCol) {
    return (
      <Typography variant='body2' sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
        Keine Öffnungszeiten hinterlegt.
      </Typography>
    );
  }

  return (
    <Paper variant='outlined' sx={{ overflowX: 'auto', borderRadius: 1.5 }}>
      <Table size='small' className='hours-table'>
        <TableHead>
          <TableRow sx={{ '& th': { bgcolor: 'action.hover', borderBottom: '2px solid', borderColor: 'divider', verticalAlign: 'top' } }}>
            <TableCell sx={{ fontWeight: 700 }}>Tag</TableCell>
            {segments.map((seg, i) => (
              <TableCell key={`${seg.from}-${i}`} sx={{ whiteSpace: 'nowrap' }}>
                <Typography variant='body2' sx={{ fontWeight: 700 }}>
                  {segmentRangeLabel(seg, horizon)}
                </Typography>
                <Stack direction='row' spacing={0.5} sx={{ mt: 0.25 }} flexWrap='wrap' useFlexGap>
                  {(seg.specialTypes || []).map(t => (
                    <Chip key={t} size='small' color='info' variant='outlined' label={SPECIAL_TYPE_LABEL[t] || t} sx={{ height: 18, fontSize: 11 }} />
                  ))}
                </Stack>
                {(seg.sourceNames || []).length > 0 && (
                  <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block' }}>
                    {seg.sourceNames.join(', ')}
                  </Typography>
                )}
              </TableCell>
            ))}
            {showGoogleCol && (
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Stack direction='row' spacing={0.5} alignItems='center'>
                  <GoogleIcon sx={{ fontSize: 15 }} />
                  <Typography variant='body2' sx={{ fontWeight: 700 }}>
                    Google (aktuell)
                  </Typography>
                </Stack>
                {google.locationTitle && (
                  <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block' }}>
                    {google.locationTitle}
                  </Typography>
                )}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {dayRows.map(row => {
            const gday = googleByWeekday.get(row.weekday);
            return (
              <TableRow
                key={row.weekday}
                sx={{ '&:nth-of-type(even)': { bgcolor: 'action.hover' }, '&:last-child td': { border: 0 } }}
              >
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{row.label}</TableCell>
                {segments.map((seg, i) => {
                  const d = seg.days.find(x => x.weekday === row.weekday);
                  const cellEditable = Boolean(d && d.sourceId && canEditSource(d.sourceId));
                  return (
                    <TableCell key={`${seg.from}-${i}`} sx={{ whiteSpace: 'nowrap' }}>
                      <Stack direction='row' spacing={0.5} alignItems='flex-start'>
                        <Tooltip title={d?.sourceName ? `Plan: ${d.sourceName}` : ''} placement='top'>
                          <Typography
                            variant='body2'
                            component='span'
                            sx={{ color: d && !d.isClosed ? 'text.primary' : 'text.disabled' }}
                          >
                            {fmtIntervals(d?.intervals)}
                          </Typography>
                        </Tooltip>
                        {cellEditable && (
                          <Tooltip title='Öffnungszeiten bearbeiten'>
                            <IconButton
                              size='small'
                              className='no-print'
                              sx={{ p: 0.25, mt: '-2px' }}
                              onClick={() =>
                                onEditCell({
                                  entertainmentId: item.entertainmentId,
                                  entertainmentName: item.name,
                                  weekday: d.weekday,
                                  weekdayLabel: d.label,
                                  segmentLabel: segmentRangeLabel(seg, horizon),
                                  sourceId: d.sourceId,
                                  sourceName: d.sourceName,
                                  intervals: d.intervals,
                                })
                              }
                            >
                              <EditIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  );
                })}
                {showGoogleCol && (
                  <TableCell
                    sx={{
                      whiteSpace: 'nowrap',
                      bgcolor: gday?.discrepant ? DISCREPANCY_BG : 'transparent',
                      color: gday?.discrepant ? 'error.main' : gday && !gday.googleClosed ? 'text.primary' : 'text.disabled',
                      fontWeight: gday?.discrepant ? 600 : 400,
                    }}
                  >
                    <Stack direction='row' spacing={0.5} alignItems='center'>
                      {gday?.discrepant && (
                        <Tooltip title='Google weicht von unseren aktuellen Öffnungszeiten ab'>
                          <WarningAmberIcon sx={{ fontSize: 15, color: 'error.main' }} />
                        </Tooltip>
                      )}
                      <span>{fmtIntervals(gday?.google)}</span>
                    </Stack>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
}

function EntertainmentBlock({
  item,
  horizon,
  canEditSource,
  onEditCell,
  canGoogleUpdate,
  busy,
  onPushInternal,
  onEditGoogle,
}) {
  const google = item.google;
  const mapped = Boolean(item.googleLocationName);
  const googleReady = Boolean(google && google.configured !== false);

  return (
    <Box
      sx={{
        mb: 2.5,
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
      className='entertainment-block'
    >
      <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap sx={{ mb: 1.5 }}>
        <Typography variant='h6' sx={{ fontWeight: 700 }}>
          {item.name}
        </Typography>
        {googleReady && google.googleMapsUri && (
          <Link href={google.googleMapsUri} target='_blank' rel='noopener' sx={{ display: 'inline-flex', verticalAlign: 'middle' }}>
            <OpenInNewIcon sx={{ fontSize: 16 }} />
          </Link>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <GoogleStatusChip google={google} mapped={mapped} />
      </Stack>

      {googleReady && google.error && (
        <Alert severity='error' sx={{ mb: 1 }} className='no-print'>
          Google-Öffnungszeiten konnten nicht geladen werden: {google.error}
        </Alert>
      )}

      <SegmentsTable
        item={item}
        horizon={horizon}
        canEditSource={canEditSource}
        onEditCell={onEditCell}
      />

      {googleReady && mapped && (
        <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }} className='no-print'>
          <Tooltip
            title={
              !google.hasInternal
                ? 'Es sind keine internen Öffnungszeiten hinterlegt.'
                : !google.hasDiscrepancy
                  ? 'Google ist bereits synchron.'
                  : ''
            }
          >
            <span>
              <Button
                variant='contained'
                size='small'
                startIcon={busy ? <CircularProgress size={16} color='inherit' /> : <SyncAltIcon />}
                onClick={() => onPushInternal(item)}
                disabled={!canGoogleUpdate || busy || !google.hasInternal || !google.hasDiscrepancy}
              >
                Interne Zeiten auf Google übertragen
              </Button>
            </span>
          </Tooltip>
          <Button
            variant='outlined'
            size='small'
            startIcon={<EditIcon />}
            onClick={() => onEditGoogle(item)}
            disabled={!canGoogleUpdate || busy || !!google.error}
          >
            Google-Zeiten bearbeiten
          </Button>
        </Box>
      )}
    </Box>
  );
}

function LocationBlock({
  location,
  horizon,
  canEditSource,
  onEditCell,
  canGoogleUpdate,
  busyId,
  onPushInternal,
  onEditGoogle,
}) {
  return (
    <Paper
      elevation={0}
      sx={{ mb: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}
      className='location-block'
    >
      <Stack
        direction='row'
        spacing={1.5}
        alignItems='center'
        sx={{ px: 2, py: 1.5, bgcolor: 'primary.main', color: 'primary.contrastText' }}
        className='location-header'
      >
        <PlaceIcon />
        <Typography variant='h5' sx={{ fontWeight: 800, flexGrow: 1 }}>
          {location.branchName}
        </Typography>
        <Chip
          size='small'
          label={`${location.entertainments.length} Entertainment(s)`}
          sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'inherit', fontWeight: 600 }}
        />
      </Stack>
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
        {location.entertainments.map(item => (
          <EntertainmentBlock
            key={item.entertainmentId}
            item={item}
            horizon={horizon}
            canEditSource={canEditSource}
            onEditCell={onEditCell}
            canGoogleUpdate={canGoogleUpdate}
            busy={busyId === item.entertainmentId}
            onPushInternal={onPushInternal}
            onEditGoogle={onEditGoogle}
          />
        ))}
      </Box>
    </Paper>
  );
}

export default function OpeningHoursOverview() {
  const authCtx = useContext(AuthContext);
  const { getOpeningHoursOverview } = useOpeningHoursOverview();
  const { updateOpeningHours } = useGoogleBusiness();
  const apiRef = useRef();
  apiRef.current = { getOpeningHoursOverview, updateOpeningHours };

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [branchId, setBranchId] = useState('');
  const [search, setSearch] = useState('');
  const [months, setMonths] = useState(12);
  const [busyId, setBusyId] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [matchOpen, setMatchOpen] = useState(false);
  const [hoursEdit, setHoursEdit] = useState(null);

  // Full opening-hours access, the former Google-view right, or the
  // branch-scoped editing right all grant viewing — the last one sees only its
  // own branches (the backend filters them out).
  const canRead =
    authCtx.checkRight('openingHour:read') ||
    authCtx.checkRight('googleBusiness:read') ||
    authCtx.checkRight('openingHour:special:updateAssignedBranch');
  const canEditAll = authCtx.checkRight('openingHour:update');
  const canGoogleUpdate = authCtx.checkRight('googleBusiness:update');

  // Which schedules the caller may edit comes from the response, not from the
  // rights: only the backend knows which ones are exclusive to their branches.
  const editScope = data?.edit;
  const allowedIds = useMemo(
    () =>
      editScope && editScope.allowedOpeningHourIds
        ? new Set(editScope.allowedOpeningHourIds)
        : null,
    [editScope]
  );
  const canEditSource = useCallback(
    sourceId => {
      // No edit block at all means a backend that does not know the scope yet
      // (deployed after this app, or rolled back). Falling through to `false`
      // here would take editing away from everyone, so the plain right decides.
      if (!editScope) return canEditAll;
      if (editScope.scope === 'none') return false;
      if (!allowedIds) return true;
      return Boolean(sourceId && allowedIds.has(sourceId));
    },
    [editScope, allowedIds, canEditAll]
  );

  const load = async (m = months) => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await apiRef.current.getOpeningHoursOverview({ months: m });
      if (res) setData(res);
    } catch (err) {
      setLoadError(true);
      toast.error('Fehler beim Laden der Öffnungszeiten-Übersicht');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canRead) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMonthsChange = value => {
    setMonths(value);
    load(value);
  };

  // After an in-place edit or a decouple the segments must be recomputed.
  const handleHoursSaved = () => {
    setHoursEdit(null);
    load();
  };

  // Apply the branch filter and a case-insensitive entertainment-name search to
  // the returned locations without mutating the source data.
  const filteredLocations = useMemo(() => {
    if (!data) return [];
    const term = search.trim().toLowerCase();
    return (data.locations || [])
      .filter(loc => !branchId || loc.branchId === branchId)
      .map(loc => {
        if (!term) return loc;
        const entertainments = (loc.entertainments || []).filter(it =>
          (it.name || '').toLowerCase().includes(term)
        );
        return { ...loc, entertainments };
      })
      .filter(loc => loc.entertainments.length > 0);
  }, [data, branchId, search]);

  const allItems = useMemo(() => {
    if (!data) return [];
    return [...(data.locations || []).flatMap(loc => loc.entertainments || []), ...(data.unassigned || [])];
  }, [data]);

  const googleSummary = useMemo(() => {
    if (!data?.google?.available || !data?.google?.configured) return null;
    const mapped = allItems.filter(it => it.googleLocationName && it.google);
    return {
      withDiff: mapped.filter(it => it.google.hasDiscrepancy).length,
      synced: mapped.filter(it => it.google.hasInternal && !it.google.hasDiscrepancy).length,
      mapped: mapped.length,
    };
  }, [data, allItems]);

  // Replaces one item's Google comparison after a successful write to Google.
  const applyUpdatedGoogle = (entertainmentId, updated) =>
    setData(prev => {
      if (!prev) return prev;
      const patchItem = it =>
        it.entertainmentId === entertainmentId && it.google
          ? {
              ...it,
              google: {
                ...it.google,
                days: updated.days,
                hasDiscrepancy: updated.hasDiscrepancy,
                hasInternal: updated.hasInternal,
                error: null,
              },
            }
          : it;
      return {
        ...prev,
        locations: (prev.locations || []).map(loc => ({
          ...loc,
          entertainments: (loc.entertainments || []).map(patchItem),
        })),
        unassigned: (prev.unassigned || []).map(patchItem),
      };
    });

  const handlePushInternal = async item => {
    setBusyId(item.entertainmentId);
    try {
      const res = await apiRef.current.updateOpeningHours(item.entertainmentId, { source: 'internal' });
      applyUpdatedGoogle(item.entertainmentId, res);
      toast.success('Interne Öffnungszeiten an Google übertragen');
    } catch (_) {
      // toast handled by hook
    } finally {
      setBusyId(null);
    }
  };

  const handleSaveEdit = async week => {
    if (!editItem) return;
    setSavingEdit(true);
    try {
      const res = await apiRef.current.updateOpeningHours(editItem.entertainmentId, { week });
      applyUpdatedGoogle(editItem.entertainmentId, res);
      toast.success('Google-Öffnungszeiten gespeichert');
      setEditItem(null);
    } catch (_) {
      // toast handled by hook
    } finally {
      setSavingEdit(false);
    }
  };

  if (!canRead) return <Navigate to='/welcome' replace />;

  const horizon = data ? { from: data.from, to: data.to } : null;
  const googleAvailable = Boolean(data?.google?.available);
  const googleConfigured = Boolean(data?.google?.configured);

  return (
    <Box sx={viewRootTextPrimarySx} className='opening-hours-print'>
      {/* Print rules: only the overview is printed, the app shell is hidden. */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .opening-hours-print, .opening-hours-print * { visibility: visible !important; }
          .opening-hours-print { position: absolute; top: 0; left: 0; width: 100%; padding: 0 !important; }
          .no-print { display: none !important; }
          .location-block { break-inside: avoid-page; box-shadow: none !important; border: 1px solid #ccc !important; }
          .location-header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .entertainment-block { break-inside: avoid; }
          .hours-table { font-size: 11px; }
          @page { margin: 12mm; size: landscape; }
        }
      `}</style>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ md: 'center' }}
        justifyContent='space-between'
        sx={{ mb: 2 }}
        className='no-print'
      >
        <Box>
          <Typography variant='h4' sx={{ fontWeight: 800 }}>
            Öffnungszeiten-Übersicht
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Effektive Öffnungszeiten je Zeitspanne – gruppiert nach Standort, inkl. Google-Abgleich
            {data?.generatedAt ? ` · Stand ${dayjs(data.generatedAt).format('DD.MM.YYYY HH:mm')}` : ''}
          </Typography>
        </Box>
        <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
          {googleAvailable && (
            <Button
              variant='outlined'
              startIcon={<StorefrontIcon />}
              onClick={() => setMatchOpen(true)}
            >
              Standorte zuordnen
            </Button>
          )}
          <Button
            variant='outlined'
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={() => load()}
            disabled={loading}
          >
            Aktualisieren
          </Button>
          <Button
            variant='contained'
            startIcon={<PictureAsPdfIcon />}
            onClick={() => window.print()}
            disabled={loading || !filteredLocations.length}
          >
            Als PDF exportieren
          </Button>
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems='center' sx={{ mb: 3 }} className='no-print'>
        <TextField
          select
          label='Standort'
          value={branchId}
          onChange={e => setBranchId(e.target.value)}
          sx={{ minWidth: 240 }}
          size='small'
        >
          <MenuItem value=''>Alle Standorte</MenuItem>
          {(data?.locations || []).map(loc => (
            <MenuItem key={loc.branchId} value={loc.branchId}>
              {loc.branchName}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label='Entertainment suchen'
          value={search}
          onChange={e => setSearch(e.target.value)}
          size='small'
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon fontSize='small' />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          label='Zeitraum'
          value={months}
          onChange={e => handleMonthsChange(Number(e.target.value))}
          sx={{ minWidth: 160 }}
          size='small'
        >
          {[3, 6, 12, 24].map(m => (
            <MenuItem key={m} value={m}>
              {m} Monate
            </MenuItem>
          ))}
        </TextField>
        {googleSummary && (
          <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
            <Chip
              size='small'
              color={googleSummary.withDiff > 0 ? 'error' : 'default'}
              variant={googleSummary.withDiff > 0 ? 'filled' : 'outlined'}
              icon={googleSummary.withDiff > 0 ? <WarningAmberIcon /> : undefined}
              label={`${googleSummary.withDiff} mit Google-Abweichungen`}
            />
            <Chip size='small' color='success' variant='outlined' label={`${googleSummary.synced} synchron`} />
            <Chip size='small' variant='outlined' label={`${googleSummary.mapped} mit Google verknüpft`} />
          </Stack>
        )}
      </Stack>

      {googleAvailable && !googleConfigured && !loading && (
        <Alert severity='warning' sx={{ mb: 2 }} className='no-print'>
          Das Google-Business-Profil ist nicht konfiguriert. Öffnungszeiten können nicht mit Google
          abgeglichen werden.
        </Alert>
      )}

      {/* Print-only header */}
      <Box sx={{ display: 'none', '@media print': { display: 'block', mb: 3 } }}>
        <Typography variant='h4' sx={{ fontWeight: 800 }}>
          Öffnungszeiten-Übersicht
        </Typography>
        {data?.generatedAt && (
          <Typography variant='body2'>
            Stand {dayjs(data.generatedAt).format('DD.MM.YYYY HH:mm')}
            {horizon ? ` · Zeitraum ${fmtDate(horizon.from)} – ${fmtDate(horizon.to)}` : ''}
          </Typography>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : loadError ? (
        <Alert severity='error' className='no-print'>
          Die Öffnungszeiten-Übersicht konnte nicht geladen werden.
        </Alert>
      ) : !filteredLocations.length && !(data?.unassigned || []).length ? (
        <Alert severity='info' className='no-print'>
          Keine Öffnungszeiten gefunden.
        </Alert>
      ) : (
        <>
          {filteredLocations.map(loc => (
            <LocationBlock
              key={loc.branchId}
              location={loc}
              horizon={horizon}
              canEditSource={canEditSource}
              onEditCell={setHoursEdit}
              canGoogleUpdate={canGoogleUpdate}
              busyId={busyId}
              onPushInternal={handlePushInternal}
              onEditGoogle={item =>
                setEditItem({
                  entertainmentId: item.entertainmentId,
                  longName: item.name,
                  locationTitle: item.google?.locationTitle || '',
                  hasInternal: Boolean(item.google?.hasInternal),
                  days: item.google?.days || [],
                })
              }
            />
          ))}
          {!branchId && !search.trim() && (data?.unassigned || []).length > 0 && (
            <LocationBlock
              location={{ branchId: 'unassigned', branchName: 'Ohne Standort', entertainments: data.unassigned }}
              horizon={horizon}
              canEditSource={canEditSource}
              onEditCell={setHoursEdit}
              canGoogleUpdate={canGoogleUpdate}
              busyId={busyId}
              onPushInternal={handlePushInternal}
              onEditGoogle={item =>
                setEditItem({
                  entertainmentId: item.entertainmentId,
                  longName: item.name,
                  locationTitle: item.google?.locationTitle || '',
                  hasInternal: Boolean(item.google?.hasInternal),
                  days: item.google?.days || [],
                })
              }
            />
          )}
        </>
      )}

      <HoursEditDialog
        open={!!hoursEdit}
        context={hoursEdit}
        onClose={() => setHoursEdit(null)}
        onSaved={handleHoursSaved}
      />

      <GoogleHoursEditDialog
        open={!!editItem}
        item={editItem}
        onClose={() => setEditItem(null)}
        onSave={handleSaveEdit}
        saving={savingEdit}
      />

      <GoogleRatingMatchDialog open={matchOpen} onClose={() => setMatchOpen(false)} onConfirmed={() => load()} />
    </Box>
  );
}
